'use client';

import { useEffect, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDevSwitchIsOn from './useDevSwitchIsOn';
import useIsMainnet from './useIsMainnet';
import { GEOBLOCK_LIST } from '@drift/common';
import { GEOLOCATION_URL } from '../constants/constants';

const getCountry = async () => {
	try {
		const result = await fetch(GEOLOCATION_URL, {
			cache: 'no-cache',
		});

		if (!result.ok) {
			// handle error
			return;
		}

		const country_code = await result.text();

		return country_code;
	} catch (_e) {
		// Do nothing.
	}
};

const checkIfCountryIsBlocked = (countryCode: string) => {
	if (process.env.NEXT_PUBLIC_IGNORE_GEOBLOCK === 'true') {
		return false;
	}

	const countryIsBlocked =
		GEOBLOCK_LIST.find((country) => country.code === countryCode) && true;

	return countryIsBlocked;
};

const useGeolocation = () => {
	const setState = useDriftStore((s) => s.set);
	const isMainnnet = useIsMainnet();
	const devswitchIsOn = useDevSwitchIsOn();
	const [countryCode, setCountryCode] = useState('');

	const onlyGeoBlockMainnet =
		process.env.NEXT_PUBLIC_ONLY_GEOBLOCK_MAINNET === 'true';

	const fetchGeolocation = async () => {
		const fetchedCountryCode = await getCountry();
		if (fetchedCountryCode) {
			setCountryCode(fetchedCountryCode);

			// Add to geolocation history
			setState((s) => {
				s.geolocationHistory.push(fetchedCountryCode);
			});
		}
	};

	useEffect(() => {
		fetchGeolocation();

		// Refetch when user returns to tab (common when switching VPN)
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				fetchGeolocation();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	useEffect(() => {
		const ignoreGeoBlock =
			process.env.NEXT_PUBLIC_IGNORE_GEOBLOCK === 'true' || devswitchIsOn;

		if ((onlyGeoBlockMainnet && !isMainnnet) || ignoreGeoBlock) {
			setState((s) => {
				s.isGeoblocked = false;
			});
		} else {
			const countryIsBlocked = checkIfCountryIsBlocked(countryCode);
			setState((s) => {
				s.countryCode = countryCode;
				s.isGeoblocked = countryIsBlocked ?? false;
			});
		}
	}, [onlyGeoBlockMainnet, isMainnnet, devswitchIsOn, countryCode]);

	return;
};

export default useGeolocation;
